'use client';

import { CerLogo } from '@slims/ui/atoms';
import {
	AppHeaderButton,
	type AppHeaderMenuItem,
	AppHeader as AppHeaderUi,
} from '@slims/ui/organisms';
import { Moon, Shield, Sun, User } from 'lucide-react';
import { useMemo } from 'react';
import Link from '@/lib/next/link';
import { usePathname, useRouter } from '@/lib/next/navigation';
import { NotificationsBell } from '@/components/layout/notifications-bell';
import { ScopeSelector } from '@/components/layout/scope-selector';
import { useCerBranding } from '@/hooks/use-cer-branding';
import { isAdminPath } from '@/lib/admin-routes';
import { hasRoleAtLeast } from '@slims/shared/auth';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';

const ROLE_LABELS: Record<string, string> = {
	superadmin: 'Super Admin',
	admin: 'Amministratore',
	referent: 'Referente',
	member: 'Socio',
};

export function AppHeader() {
	const router = useRouter();
	const pathname = usePathname();
	const openModal = useUIStore((s) => s.openModal);
	const theme = useUIStore((s) => s.theme);
	const toggleTheme = useUIStore((s) => s.toggleTheme);
	const { role, fullName, email, avatarUrl } = useAuthStore();
	const branding = useCerBranding();

	const isAdmin = !!role && hasRoleAtLeast(role, 'admin');
	const inAdminArea = isAdminPath(pathname);

	const menuItems = useMemo<AppHeaderMenuItem[]>(
		() => [
			{
				id: 'profile',
				label: 'Il mio profilo',
				icon: <User />,
				onClick: () => openModal('profile'),
			},
		],
		[openModal],
	);

	return (
		<AppHeaderUi
			linkComponent={Link}
			logoSlot={
				<CerLogo
					theme={theme}
					logoUrl={branding?.logoUrl}
					appName={branding?.appName}
					href={role === 'member' ? '/myslims' : '/dashboard'}
					linkComponent={Link}
				/>
			}
			actionsSlot={
				<>
					{isAdmin && (
						<AppHeaderButton
							icon={<Shield />}
							label={inAdminArea ? "Torna all'app" : 'Admin'}
							active={inAdminArea}
							onClick={() => router.push(inAdminArea ? '/dashboard' : '/cer')}
						/>
					)}
					<AppHeaderButton
						icon={theme === 'light' ? <Moon /> : <Sun />}
						title={theme === 'light' ? 'Passa al tema scuro' : 'Passa al tema chiaro'}
						aria-label="Cambia tema"
						onClick={toggleTheme}
					/>
				</>
			}
			notificationsSlot={role === 'member' ? undefined : <NotificationsBell />}
			scopeSelectorSlot={inAdminArea ? undefined : <ScopeSelector />}
			fullName={fullName}
			email={email}
			avatarUrl={avatarUrl}
			roleLabel={ROLE_LABELS[role ?? ''] ?? role ?? ''}
			menuItems={menuItems}
		/>
	);
}
